import { useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { MobileFrame } from './shell/MobileFrame';
import { MobileTabBar } from './shell/MobileTabBar';
import { ScanShell } from './shell/ScanShell';
import { MobileScanContext } from './shell/MobileScanContext';
import { ImpersonationBanner } from './shell/ImpersonationBanner';
import { useAuth } from '@/lib/auth';
import type { MobileTab } from './types/index';
import type { ReactNode } from 'react';

interface MobileRootProps {
  children: ReactNode;
}

const TAB_ROUTES = {
  home: '/m/',
  lancamentos: '/m/lancamentos',
  prestacao: '/m/prestacao',
  eu: '/m/eu',
} as const;

function pathToTab(pathname: string): MobileTab {
  if (pathname.startsWith('/m/lancamentos')) return 'lancamentos';
  if (pathname.startsWith('/m/prestacao')) return 'prestacao';
  if (pathname.startsWith('/m/eu')) return 'eu';
  return 'home';
}

/**
 * Provider de navegação mobile + shell principal.
 * Gerencia a tab ativa (derivada da rota atual) e o overlay do scanner.
 */
export function MobileRoot({ children }: MobileRootProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigate = useNavigate() as any;
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;
  const { isImpersonating, user } = useAuth();

  const currentTab = pathToTab(pathname);
  const [scanOpen, setScanOpen] = useState(false);

  function handleTabChange(tab: MobileTab) {
    navigate({ to: TAB_ROUTES[tab] });
  }

  return (
    <MobileScanContext.Provider value={{ openScan: () => setScanOpen(true) }}>
      <MobileFrame>
        {isImpersonating && (
          <ImpersonationBanner
            workspaceName={user?.workspaceName ?? 'workspace'}
            onExit={() => navigate({ to: '/m/master' })}
          />
        )}
        {/* Área de conteúdo das rotas */}
        <div
          style={{
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {children}
        </div>

        <MobileTabBar
          current={currentTab}
          onChange={handleTabChange}
          onScan={() => setScanOpen(true)}
        />

        {scanOpen && <ScanShell onClose={() => setScanOpen(false)} />}
      </MobileFrame>
    </MobileScanContext.Provider>
  );
}
